import React, { useEffect, useState } from 'react';
import { DatePicker } from '@cambridgeassessment/cambridge-ui';
import { DATE_DISPLAY_FORMAT } from '../../../../constants';
import { StyledInputContainer, StyledTitle } from '../Series.styles';
import { convertToMidnight, Series, buildNow, Roles } from '@cambridgeassessment/lib-ee-commons';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import thunks from '../../../../store/series/thunks';
import { RootState } from '../../../../store';
import { Authorized } from 'lib-browser';

interface StartDateProps {
  series: Series | null;
  startDate?: Series['startDate'];
  componentSeriesStartDates?: string[];
}

const today = buildNow()().toISOString();

const StartDate: React.FC<StartDateProps> = ({ series, startDate, componentSeriesStartDates }) => {
  const dispatch = useDispatch();
  const { seriesDetails } = useSelector((state: RootState) => state.series);
  const selectedRole = useSelector((state: RootState) => state.userData.selectedRole);

  useEffect(() => {
    if (series && series.id) {
      setErrorMessage('');
    }
  }, [series]);

  const [errorMessage, setErrorMessage] = useState<string>('');

  if (!series) {
    return null;
  }

  if (!startDate) {
    startDate = moment(today).add(1, 'days').toISOString();
  }

  const marksBufferDates = seriesDetails?.marksSubmissionWindows?.map((window) => window.buffer) ?? '';
  const workBufferDates = seriesDetails?.submissionWindows?.map((window) => window.buffer) ?? '';
  const value = moment.parseZone(startDate).toISOString();
  const activeSeries = !!startDate && startDate < today;
  const earliestComponentSeriesDate =
    (componentSeriesStartDates?.length &&
      componentSeriesStartDates.reduce((a, b) => {
        return a && b && a < b ? a : b;
      })) ??
    '';
  const earliestBufferDate =
    [...marksBufferDates, ...workBufferDates].reduce((a, b) => {
      return a && b && a < b ? a : b;
    }) ?? '';

  const isChosenDateValid = (date: string): boolean => {
    const earliestBufferDateForComparing = moment(earliestBufferDate).subtract(2, 'days').toISOString();
    if (date < convertToMidnight(today)) {
      setErrorMessage('Cannot be earlier than today’s date.');
      return false;
    }
    if (earliestComponentSeriesDate && date > convertToMidnight(earliestComponentSeriesDate)) {
      setErrorMessage('Cannot be later than the earliest date of a submission window linked to the series.');
      return false;
    }
    if (date > earliestBufferDateForComparing) {
      setErrorMessage('Must be at least two days earlier than either the work or marks ‘buffer’ date.');
      return false;
    }
    setErrorMessage('');
    return true;
  };

  const handleUpdate = (date: moment.Moment) => {
    const newDate = date.parseZone();
    const validDate = isChosenDateValid(newDate.toISOString());
    if (!validDate) {
      return;
    }
    dispatch(thunks.updateSeriesDateByFieldName(series._id, newDate, 'startDate'));
  };

  const RenderComponent = (props?: { readOnly?: boolean }) => (
    <StyledInputContainer>
      <StyledTitle data-qa-id='series-start-date'>Series start date</StyledTitle>
      <DatePicker
        dataQaId={`${series.name}-startDate-datePicker`}
        key={startDate}
        onChange={handleUpdate}
        value={convertToMidnight(value)}
        format={DATE_DISPLAY_FORMAT}
        disabled={activeSeries || props?.readOnly}
        helperText={
          activeSeries
            ? 'Series Start Date cannot be changed once a series is active'
            : 'Series start date must be before submission window start date'
        }
        errorMsg={errorMessage}
      />
    </StyledInputContainer>
  );

  return (
    <Authorized
      selectedRole={selectedRole}
      roles={[Roles.SUBMIT_SUPER_ADMIN]}
      readOnlyRoles={[Roles.SUBMIT_COMPLIANCE, Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER, Roles.SUBMIT_ADMINISTRATOR]}
    >
      <RenderComponent />
    </Authorized>
  );
};

export default StartDate;
